import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ChevronDown,
  Github,
  Linkedin,
  Mail,
  Globe,
  ExternalLink,
  Code2,
  Briefcase,
  GraduationCap,
  Award,
  Calendar,
  MapPin,
  Moon,
  Sun,
  Menu,
  X,
  ArrowUp
} from 'lucide-react';

const PortfolioImproved = () => {
  const [darkMode, setDarkMode] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [activeImageIndex, setActiveImageIndex] = useState({});
  const [isPaused, setIsPaused] = useState({});

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  // State for portfolio data
  const [portfolioData, setPortfolioData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch portfolio data from remote API or fallback to JSON file
  useEffect(() => {
    const fetchPortfolioData = async () => {
      try {
        // Try to fetch from remote API first (for dynamic database updates)
        try {
          // Attempt to fetch from remote Node-RED API
          const response = await fetch('https://nodered.frrut.com/resume');
          if (response.ok) {
            const data = await response.json();
            setPortfolioData(data);
            setLoading(false);
            console.log('Portfolio data fetched from remote API (https://nodered.frrut.com/resume)');
            return;
          }
        } catch (apiError) {
          console.log('Remote API fetch failed, falling back to static JSON:', apiError.message);
        }

        // Fallback to static JSON file if API is not available
        const portfolioModule = await import('./resume/portfolio.json');
        const data = portfolioModule.default;
        setPortfolioData(data);
        setLoading(false);
        console.log('Portfolio data loaded from static JSON (fallback)');
      } catch (error) {
        console.error('Error fetching portfolio data:', error);
        setLoading(false);
      }
    };

    fetchPortfolioData();

    // Optional: Refresh data every 5 minutes to get database updates
    const refreshInterval = setInterval(() => {
      fetchPortfolioData();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(refreshInterval);
  }, []);

  // Auto-play carousel effect
  useEffect(() => {
    if (!portfolioData) return;

    const intervals = {};

    portfolioData.projects.forEach((project, idx) => {
      if (project.image_urls && project.image_urls.length > 1 && !isPaused[idx]) {
        intervals[idx] = setInterval(() => {
          setActiveImageIndex(prev => ({
            ...prev,
            [idx]: ((prev[idx] || 0) + 1) % project.image_urls.length
          }));
        }, 3000); // Change image every 3 seconds
      }
    });

    return () => {
      Object.values(intervals).forEach(interval => clearInterval(interval));
    };
  }, [isPaused, portfolioData]);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">載入中...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (!portfolioData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400">載入作品集資料時發生錯誤</p>
        </div>
      </div>
    );
  }

  // Extract data from loaded portfolio
  const personalInfo = {
    name: portfolioData.personal?.name || "翁志豪",
    title: portfolioData.profile.title,
    subtitle: portfolioData.profile.subtitle,
    email: portfolioData.personal?.email || "howard.weng@gmail.com",
    website: portfolioData.personal?.link || "https://portfolio.frrut.com",
    birthYear: portfolioData.personal?.birth || "1972",
  };

  const skills = portfolioData.skills.categories.reduce((acc, category) => {
    acc[category.name] = category.items;
    return acc;
  }, {});

  const projects = portfolioData.projects.map(project => {
    // More precise AI/ML detection based on actual AI technologies and capabilities
    const techString = project.technologies.join(' ').toLowerCase();
    const nameString = project.name.toLowerCase();
    const descString = project.description.toLowerCase();

    const isAIProject =
      // Core AI/ML technologies - LLM and Language Models
      techString.includes('llm') ||
      techString.includes('large language models') ||
      techString.includes('openai') ||
      techString.includes('gpt') ||
      techString.includes('gemini') ||
      techString.includes('prompt engineering') ||

      // AI Vision and Computer Vision
      techString.includes('ai vision') ||
      techString.includes('computer vision') ||
      techString.includes('opencv') ||
      techString.includes('yolov8') ||
      techString.includes('triple image analysis') ||
      techString.includes('luxury brand recognition') ||
      techString.includes('damage detection') ||

      // AI/ML Frameworks and Models
      techString.includes('pytorch') ||
      techString.includes('tensorflow') ||

      // AI-specific functionalities
      techString.includes('rag') ||
      techString.includes('vector search') ||
      techString.includes('faiss') ||
      techString.includes('stt') ||
      techString.includes('tts') ||
      techString.includes('speech-to-text') ||
      techString.includes('text-to-speech') ||
      techString.includes('natural language processing') ||

      // OCR and Document Analysis (AI-powered)
      (techString.includes('ocr') && (descString.includes('ai') || descString.includes('智慧'))) ||

      // Project names with clear AI focus
      nameString.includes('ai ') ||
      nameString.includes('greetai') ||
      nameString.includes('menuSync ai') ||

      // Chinese AI indicators combined with technical evidence
      (nameString.includes('智慧') && (
        techString.includes('ai') ||
        techString.includes('llm') ||
        techString.includes('computer vision') ||
        techString.includes('openai')
      )) ||

      // Document analysis with LLM (specific use case)
      (nameString.includes('謄本分析') && techString.includes('gemini'));

    return {
      title: project.name,
      description: project.description,
      technologies: project.technologies,
      category: isAIProject ? "AI/ML" : "Full Stack",
      images: project.image_urls.map(img => img.imgUrl)
    };
  });

  const experience = portfolioData.experience.items.map(item => ({
    title: item.position,
    company: item.company,
    duration: item.duration,
    period: item.period,
    description: item.description,
    skills: item.skills || []
  }));


  const education = [
    {
      school: "Golden Gate University（美國）",
      degree: "E-Commerce碩士畢業",
      period: "2001/8 - 2002/12"
    },
    {
      school: "國立雲林科技大學",
      degree: "機械系大學畢業",
      period: "1991/7 - 1995/7"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/80 dark:bg-gray-900/80 backdrop-blur-md z-50 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Code2 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              <span className="font-bold text-xl bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Howard Weng
              </span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {['首頁', '技能', '專案', '經歷', '學歷', '聯絡'].map((item, idx) => (
                <button
                  key={idx}
                  onClick={() => scrollToSection(['home', 'skills', 'projects', 'experience', 'education', 'contact'][idx])}
                  className="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  {item}
                </button>
              ))}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setDarkMode(!darkMode)}
                className="ml-4"
              >
                {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center space-x-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setDarkMode(!darkMode)}
              >
                {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {['首頁', '技能', '專案', '經歷', '學歷', '聯絡'].map((item, idx) => (
                <button
                  key={idx}
                  onClick={() => scrollToSection(['home', 'skills', 'projects', 'experience', 'education', 'contact'][idx])}
                  className="block w-full text-left px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center animate-fade-in">
            <h1 className="text-5xl md:text-6xl font-bold mb-4">
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {personalInfo.name}
              </span>
            </h1>
            <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
              {personalInfo.title}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto">
              {personalInfo.subtitle}
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <Button variant="gradient" size="lg" onClick={() => scrollToSection('projects')}>
                查看專案 <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
              <Button variant="outline" size="lg" onClick={() => scrollToSection('contact')}>
                聯絡我 <Mail className="ml-2 h-4 w-4" />
              </Button>
            </div>

            <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600 dark:text-gray-400">
              <a href={`mailto:${personalInfo.email}`} className="flex items-center hover:text-blue-600 transition-colors">
                <Mail className="h-4 w-4 mr-2" /> {personalInfo.email}
              </a>
              <a href={personalInfo.website} target="_blank" rel="noopener noreferrer" className="flex items-center hover:text-blue-600 transition-colors">
                <Globe className="h-4 w-4 mr-2" /> Portfolio
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-800 dark:text-gray-200">
              {portfolioData?.skills?.title || '核心技能'}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {portfolioData?.skills?.subtitle || '專精於現代化技術棧，從前端到後端，從 AI 到雲端部署的全方位技術能力'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
            {Object.entries(skills).map(([category, items], idx) => {
              // Define colors for each category - matching portfolio theme
              const categoryColors = {
                'AI & ML': {
                  bg: 'bg-gradient-to-br from-blue-600 to-purple-600',
                  icon: 'text-blue-600 dark:text-blue-400',
                  badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
                  hover: 'hover:bg-blue-200 dark:hover:bg-blue-800'
                },
                '前端開發': {
                  bg: 'bg-gradient-to-br from-blue-500 to-cyan-500',
                  icon: 'text-cyan-700 dark:text-cyan-300',
                  badge: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
                  hover: 'hover:bg-cyan-200 dark:hover:bg-cyan-800'
                },
                '後端開發': {
                  bg: 'bg-gradient-to-br from-purple-500 to-blue-600',
                  icon: 'text-purple-600 dark:text-purple-400',
                  badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
                  hover: 'hover:bg-purple-200 dark:hover:bg-purple-800'
                },
                'DevOps': {
                  bg: 'bg-gradient-to-br from-blue-600 to-indigo-600',
                  icon: 'text-indigo-600 dark:text-indigo-400',
                  badge: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
                  hover: 'hover:bg-indigo-200 dark:hover:bg-indigo-800'
                }
              };

              const colors = categoryColors[category] || categoryColors['AI & ML'];

              return (
                <Card
                  key={category}
                  className="group relative overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border-0 bg-white dark:bg-gray-800"
                  style={{ animationDelay: `${idx * 100}ms` }}
                >
                  {/* Gradient header */}
                  <div className={`h-2 ${colors.bg}`}></div>

                  <CardHeader className="pb-4 relative">
                    {/* Background pattern */}
                    <div className="absolute top-0 right-0 w-20 h-20 opacity-5">
                      <div className={`w-full h-full ${colors.bg} rounded-full transform translate-x-6 -translate-y-6`}></div>
                    </div>

                    <CardTitle className="text-xl font-bold flex items-center justify-between relative z-10">
                      <div className="flex items-center">
                        <div className={`p-2 rounded-lg ${colors.badge} mr-3`}>
                          <Award className={`h-5 w-5 ${colors.icon}`} />
                        </div>
                        <span className="text-gray-800 dark:text-gray-200">{category}</span>
                      </div>
                      {/* Move skill count to header */}
                      <span className={`text-sm font-normal ${colors.icon} bg-white dark:bg-gray-800 px-2 py-1 rounded-full border-2 border-current`}>
                        {items.length}
                      </span>
                    </CardTitle>
                    <div className="mt-2">
                      <div className={`h-1 w-16 ${colors.bg} rounded-full transition-all duration-300 group-hover:w-24`}></div>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                      {items.map((skill, skillIdx) => (
                        <Badge
                          key={skill}
                          className={`${colors.badge} ${colors.hover} transition-all duration-300 text-xs font-medium px-3 py-1.5 border-0 transform hover:scale-105 cursor-default`}
                          style={{ animationDelay: `${(idx * 100) + (skillIdx * 50)}ms` }}
                        >
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>

                  {/* Hover effect overlay */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-700"></div>
                </Card>
              );
            })}
          </div>

          {/* Skills summary stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { label: '人工智慧', count: skills['AI & ML']?.length || 0, icon: '🤖' },
              { label: '前端技術', count: skills['前端開發']?.length || 0, icon: '💻' },
              { label: '後端技術', count: skills['後端開發']?.length || 0, icon: '⚙️' },
              { label: '雲端部署', count: skills['DevOps']?.length || 0, icon: '🚀' }
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-3xl mb-2">{stat.icon}</div>
                <div className="text-2xl font-bold text-gray-800 dark:text-gray-200">{stat.count}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{stat.label}技能</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-800 dark:text-gray-200">
            最近的專案經驗
          </h2>

          {/* All Projects Grid - Consistent Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {projects.map((project, projectIdx) => {
              const currentImageIndex = activeImageIndex[projectIdx] || 0;

              return (
                <Card
                  key={projectIdx}
                  className="hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden"
                  onMouseEnter={() => setIsPaused(prev => ({ ...prev, [projectIdx]: true }))}
                  onMouseLeave={() => setIsPaused(prev => ({ ...prev, [projectIdx]: false }))}
                >
                  {project.images && project.images.length > 0 && (
                    <div className="relative h-64 md:h-80 overflow-hidden group bg-gray-100 dark:bg-gray-800">
                      {/* Image Display with smooth transition */}
                      <img
                        key={currentImageIndex}
                        src={project.images[currentImageIndex]}
                        alt={`${project.title} - ${currentImageIndex + 1}`}
                        className="w-full h-full object-contain transition-all duration-500 animate-fade-in group-hover:scale-105"
                      />

                      {/* Badges Overlay */}
                      <div className="absolute bottom-2 left-2 flex gap-2 flex-wrap">
                        {projectIdx === 0 && <Badge variant="gradient">精選專案</Badge>}
                        <Badge variant="gradient">{project.category}</Badge>
                        {project.impact && (
                          <Badge variant="success">
                            {project.impact}
                          </Badge>
                        )}
                      </div>

                      {/* Navigation Arrows (if multiple images) */}
                      {project.images.length > 1 && (
                        <>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              setActiveImageIndex(prev => ({
                                ...prev,
                                [projectIdx]: currentImageIndex === 0 ? project.images.length - 1 : currentImageIndex - 1
                              }));
                            }}
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                          >
                            <ChevronDown className="h-4 w-4 rotate-90" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              setActiveImageIndex(prev => ({
                                ...prev,
                                [projectIdx]: (currentImageIndex + 1) % project.images.length
                              }));
                            }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                          >
                            <ChevronDown className="h-4 w-4 -rotate-90" />
                          </button>

                          {/* Image Indicators with animation */}
                          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                            {project.images.map((_, imgIdx) => (
                              <button
                                key={imgIdx}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setActiveImageIndex(prev => ({
                                    ...prev,
                                    [projectIdx]: imgIdx
                                  }));
                                  setIsPaused(prev => ({ ...prev, [projectIdx]: true }));
                                  setTimeout(() => {
                                    setIsPaused(prev => ({ ...prev, [projectIdx]: false }));
                                  }, 5000); // Resume auto-play after 5 seconds
                                }}
                                className={`h-2 rounded-full transition-all ${
                                  imgIdx === currentImageIndex
                                    ? 'w-6 bg-white'
                                    : 'w-2 bg-white/50 hover:bg-white/75'
                                }`}
                              />
                            ))}
                          </div>

                          {/* Auto-play indicator */}
                          {!isPaused[projectIdx] && project.images.length > 1 && (
                            <div className="absolute bottom-2 right-2">
                              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                            </div>
                          )}
                        </>
                      )}

                      {/* Image Counter */}
                      {project.images.length > 1 && (
                        <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                          {currentImageIndex + 1} / {project.images.length}
                        </div>
                      )}
                    </div>
                  )}
                  <CardHeader className="pb-4">
                    <CardTitle className="text-xl font-bold leading-tight mb-3 text-gray-800 dark:text-gray-200">
                      {project.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                      {project.description}
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {project.technologies.map((tech) => (
                        <Badge key={tech} className="text-xs bg-gray-100 text-gray-700 border border-gray-300 px-3 py-1 hover:bg-gray-200 transition-colors">
                          {tech}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 text-gray-800 dark:text-gray-200">
            工作經歷
          </h2>
          <div className="relative">
            {/* Timeline line for desktop */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-600 via-purple-600 to-blue-600 hidden lg:block"></div>

            <div className="space-y-8">
              {experience.map((exp, idx) => (
                <div key={idx} className="relative group">
                  {/* Timeline dot for desktop */}
                  <div className="absolute left-7 w-3 h-3 bg-blue-600 rounded-full border-4 border-white dark:border-gray-900 hidden lg:block group-hover:scale-150 transition-transform"></div>

                  <Card className="ml-0 lg:ml-20 hover:shadow-2xl transition-all duration-300 border-l-4 border-blue-600 hover:border-purple-600">
                    <CardHeader className="pb-4">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                        <div className="flex-1">
                          <CardTitle className="text-2xl font-bold flex items-start mb-2">
                            <Briefcase className="h-6 w-6 mr-3 text-blue-600 flex-shrink-0 mt-0.5" />
                            <span className="leading-tight">{exp.title}</span>
                          </CardTitle>
                          <p className="text-lg text-gray-700 dark:text-gray-300 font-medium ml-9">{exp.company}</p>
                        </div>
                        <div className="flex flex-col items-start sm:items-end gap-2 ml-9 sm:ml-0">
                          <Badge variant="gradient" className="text-sm px-4 py-1.5 font-semibold">
                            {exp.duration}
                          </Badge>
                          <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                            <Calendar className="h-4 w-4 mr-1.5" />
                            <span>{exp.period}</span>
                          </p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="ml-9 space-y-3">
                        <p className="text-base text-gray-600 dark:text-gray-300 leading-relaxed">
                          {exp.description}
                        </p>
                        {exp.skills && (
                          <div className="flex flex-wrap gap-2 pt-2">
                            {exp.skills.map((skill, skillIdx) => (
                              <Badge key={skillIdx} variant="secondary" className="text-xs">
                                {skill}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Education Section */}
      <section id="education" className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-800 dark:text-gray-200">
            學歷
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {education.map((edu, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center">
                    <GraduationCap className="h-5 w-5 mr-2 text-blue-600" />
                    {edu.school}
                  </CardTitle>
                  <CardDescription>{edu.degree}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    <Calendar className="inline h-3 w-3 mr-1" />
                    {edu.period}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8 text-gray-800 dark:text-gray-200">
            聯絡方式
          </h2>
          <Card className="max-w-md mx-auto">
            <CardContent className="pt-6">
              <div className="space-y-4">
                <a href={`mailto:${personalInfo.email}`} className="flex items-center justify-center text-gray-600 dark:text-gray-300 hover:text-blue-600 transition-colors">
                  <Mail className="h-5 w-5 mr-3" />
                  {personalInfo.email}
                </a>
                <a href={personalInfo.website} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center text-gray-600 dark:text-gray-300 hover:text-blue-600 transition-colors">
                  <Globe className="h-5 w-5 mr-3" />
                  {personalInfo.website}
                </a>
              </div>
              <div className="mt-6">
                <Button variant="gradient" className="w-full">
                  發送訊息 <Mail className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <Button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 rounded-full p-3 shadow-lg animate-slide-up"
          variant="gradient"
          size="icon"
        >
          <ArrowUp className="h-5 w-5" />
        </Button>
      )}
    </div>
  );
};

export default PortfolioImproved;